import React, { useState, useEffect } from 'react';
import { Filter, SlidersHorizontal, Search } from 'lucide-react';
import axios from 'axios';
import ProductCard from './ProductCard';
import { Button } from './ui/button';
import { observeNewElements } from '../utils/scrollAnimations';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CollectionPage = ({ addToCart }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBrand, setSelectedBrand] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedGender, setSelectedGender] = useState('');
  const [selectedSize, setSelectedSize] = useState('');
  const [sortBy, setSortBy] = useState('');
  // Removed viewMode state - using grid view only
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [genders] = useState([
    { value: 'men', label: 'Men' },
    { value: 'women', label: 'Women' },
    { value: 'unisex', label: 'Unisex' },
    { value: 'kids', label: 'Kids' }
  ]);
  const [sizes] = useState([
    { value: '6', label: 'US 6' },
    { value: '6.5', label: 'US 6.5' },
    { value: '7', label: 'US 7' },
    { value: '7.5', label: 'US 7.5' },
    { value: '8', label: 'US 8' },
    { value: '8.5', label: 'US 8.5' },
    { value: '9', label: 'US 9' },
    { value: '9.5', label: 'US 9.5' },
    { value: '10', label: 'US 10' },
    { value: '10.5', label: 'US 10.5' },
    { value: '11', label: 'US 11' },
    { value: '11.5', label: 'US 11.5' },
    { value: '12', label: 'US 12' },
    { value: '13', label: 'US 13' }
  ]);

  useEffect(() => {
    fetchProducts();
    fetchBrands();
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [selectedBrand, selectedCategory, selectedGender, selectedSize, sortBy]);

  // Reinitialize scroll animations when products change
  useEffect(() => {
    if (products.length > 0) {
      const timer = setTimeout(() => {
        observeNewElements();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [products]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      if (selectedBrand) params.append('brand', selectedBrand);
      if (selectedCategory) params.append('category', selectedCategory);
      params.append('limit', '1000'); // Fetch all products
      
      const response = await axios.get(`${API}/products?${params.toString()}`);
      let filteredProducts = response.data;
      
      // Client-side filtering for gender and size (since backend doesn't have these fields yet)
      if (selectedSize) {
        filteredProducts = filteredProducts.filter(product => 
          product.variants.some(variant => variant.size === selectedSize && variant.stock_quantity > 0)
        );
      }
      
      // Note: Gender filtering would need to be implemented when gender field is added to products
      // For now, we'll keep all products regardless of selectedGender
      
      // Client-side sorting
      switch (sortBy) {
        case 'price_low':
          filteredProducts.sort((a, b) => a.price - b.price);
          break;
        case 'price_high':
          filteredProducts.sort((a, b) => b.price - a.price);
          break;
        case 'name':
          filteredProducts.sort((a, b) => a.name.localeCompare(b.name));
          break;
        case 'featured':
          filteredProducts.sort((a, b) => b.is_featured - a.is_featured);
          break;
        default:
          // No sorting - keep original order
          break;
      }
      
      setProducts(filteredProducts);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBrands = async () => {
    try {
      const response = await axios.get(`${API}/brands`);
      setBrands(response.data);
    } catch (error) {
      console.error('Error fetching brands:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const clearFilters = () => {
    setSelectedBrand('');
    setSelectedCategory('');
    setSelectedGender('');
    setSelectedSize('');
    setSortBy('');
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (selectedBrand) count++;
    if (selectedCategory) count++;
    if (selectedGender) count++;
    if (selectedSize) count++;
    if (sortBy) count++;
    return count;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center" data-testid="loading-indicator">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading collection...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="collection-page">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <h1 className="text-5xl font-bold text-gray-900 mb-6" data-testid="collection-title">
              Complete Collection
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-testid="collection-description">
              Discover our entire range of premium sneakers from legendary brands. 
              From court-ready basketball shoes to everyday lifestyle favorites.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Advanced Filters */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Brand Filter */}
            <select
              value={selectedBrand}
              onChange={(e) => setSelectedBrand(e.target.value)}
              className="border border-gray-300 rounded-lg px-4 py-2 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
              data-testid="brand-filter"
            >
              <option value="">All Brands</option>
              {brands.map((brand) => (
                <option key={brand} value={brand}>
                  {brand}
                </option>
              ))}
            </select>

            {/* Category Filter */}
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="border border-gray-300 rounded-lg px-4 py-2 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
              data-testid="category-filter"
            >
              <option value="">All Categories</option>
              {categories.map((category) => (
                <option key={category.value} value={category.value}>
                  {category.label}
                </option>
              ))}
            </select>

            {/* Gender Filter */}
            <select
              value={selectedGender}
              onChange={(e) => setSelectedGender(e.target.value)}
              className="border border-gray-300 rounded-lg px-4 py-2 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
              data-testid="gender-filter"
            >
              <option value="">All Genders</option>
              {genders.map((gender) => (
                <option key={gender.value} value={gender.value}>
                  {gender.label}
                </option>
              ))}
            </select>

            {/* Size Filter */}
            <select
              value={selectedSize}
              onChange={(e) => setSelectedSize(e.target.value)}
              className="border border-gray-300 rounded-lg px-4 py-2 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
              data-testid="size-filter"
            >
              <option value="">All Sizes</option>
              {sizes.map((size) => (
                <option key={size.value} value={size.value}>
                  {size.label}
                </option>
              ))}
            </select>

            {/* Sort */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="border border-gray-300 rounded-lg px-4 py-2 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
              data-testid="sort-select"
            >
              <option value="" disabled>Sort By:</option>
              <option value="name">A-Z</option>
              <option value="price_low">Price: Low to High</option>
              <option value="price_high">Price: High to Low</option>
              <option value="featured">Featured First</option>
            </select>
          </div>

          {/* Active Filters & Clear */}
          {getActiveFiltersCount() > 0 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
              <div className="flex items-center space-x-2">
                <Filter className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-600">
                  {getActiveFiltersCount()} filter{getActiveFiltersCount() > 1 ? 's' : ''} active
                </span>
              </div>
              <Button
                onClick={clearFilters}
                variant="outline"
                size="sm"
                data-testid="clear-filters"
              >
                Clear All Filters
              </Button>
            </div>
          )}
        </div>

        {/* Results Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-3xl font-bold text-gray-900" data-testid="results-count">
              {products.length} {products.length === 1 ? 'Sneaker' : 'Sneakers'}
            </h2>
            {(selectedGender || selectedBrand || selectedSize) && (
              <p className="text-gray-600 mt-1" data-testid="filter-results-info">
                Filtered results
                {selectedGender && ` for ${genders.find(g => g.value === selectedGender)?.label}`}
                {selectedBrand && ` by ${selectedBrand}`}
                {selectedSize && ` in size ${sizes.find(s => s.value === selectedSize)?.label}`}
              </p>
            )}
          </div>
        </div>

        {/* Products Grid */}
        {products.length > 0 ? (
          <div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
            data-testid="products-container"
          >
            {products.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                onAddToCart={addToCart}
                compact={false}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-20" data-testid="no-products-found">
            <div className="max-w-md mx-auto">
              <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Search className="w-10 h-10 text-gray-400" />
              </div>
              <h3 className="text-2xl font-medium text-gray-900 mb-3">No sneakers found</h3>
              <p className="text-gray-500 mb-8 leading-relaxed">
                We couldn't find any products matching your criteria. 
                Try adjusting your filters or search terms.
              </p>
              <Button onClick={clearFilters} className="bg-brand-purple hover:bg-brand-purple-dark text-white px-8 py-3">
                View All Products
              </Button>
            </div>
          </div>
        )}

        {/* Collection Stats */}
        {products.length > 0 && (
          <div className="mt-16 bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 text-center">
              <div>
                <div className="text-3xl font-bold text-gray-900 mb-2">
                  {products.length}
                </div>
                <div className="text-gray-600">Total Products</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-gray-900 mb-2">
                  {brands.length}
                </div>
                <div className="text-gray-600">Premium Brands</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-gray-900 mb-2">
                  {genders.length}
                </div>
                <div className="text-gray-600">Gender Options</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-gray-900 mb-2">
                  {products.filter(p => p.is_featured).length}
                </div>
                <div className="text-gray-600">Featured Items</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CollectionPage;